/**
 * Where an actor can EVER stand — the pack's answer, read from its
 * declarations (D233, actors round C; D238, the follower;
 * `docs/design/actors.md` §5, §7).
 *
 * @remarks
 * An actor stands where its `initialRoomId` put it, wherever a `move_actor`
 * can carry it, and — D238 — wherever it can be LED: a follower goes where
 * the player goes, so from every room it can stand in, every room the player
 * can go on to that it does not always refuse is a room it can stand in too.
 * Three packages need that set and must agree about it, which is why it is
 * computed once, here, below all of them:
 *
 * - the loader asks it to know whether an actor is ever ON STAGE (one with
 *   neither a starting room nor a move onto one is dead content), whether a
 *   `blocksExits` names an exit of a room the body can reach, and whether
 *   `actor_present` / `actor_addressable` / `actor_in_room` can ever hold;
 * - the prose audit asks it to know in which rooms an actor's words are
 *   referable (an arrival's nouns are promised in the room it arrives in, and
 *   a follower's in every room it can be led to);
 * - the engine's static room catalog asks it for the same reason, so the
 *   audit's dry run and the live catalog name the same bodies per room.
 *
 * Judged from the document alone, at the tier the loader calls static: a
 * move that EXISTS counts, whether or not anything in play ever fires it; an
 * exit counts whatever its lock says (the graph rule's own reading, D61); a
 * refusal arm with a `when` might not hold, so only an arm without one keeps
 * the follower out.
 *
 * @packageDocumentation
 */

import type { ActorDefinition } from './actors.js';
import type { ContentPackDefinition } from './content-pack.js';
import type { MoveActorEffect } from './effects.js';
import type { RoomId } from './identifiers.js';

import { everyEffectOf } from './effect-homes.js';

/**
 * The pack's stage directions — everything that can put a body in a room,
 * read once: the `move_actor`s that carry a body, and the ways the player
 * goes from room to room, which carry a follower.
 */
export interface StageDirections {
	/** Every `move_actor` the pack authors, anywhere effects live, in document order. */
	readonly actorMoves: readonly MoveActorEffect[];
	/**
	 * The rooms the player can go on to from each room: its exits' targets
	 * (hidden or locked, as the graph rule reads them) and every room a
	 * `move_player` names, since a forced move can start anywhere.
	 */
	readonly playerRoutes: ReadonlyMap<RoomId, readonly RoomId[]>;
}

/**
 * Read the pack's stage directions once — the input to
 * {@link roomsAnActorCanStandIn} for every actor.
 *
 * @param contentPack - The parsed pack
 * @returns The moves and the player's routes
 */
export const stageDirectionsOf = (contentPack: ContentPackDefinition): StageDirections => {
	const effects = everyEffectOf(contentPack);
	const forcedMoveTargets = effects.flatMap((effect) => (effect.type === 'move_player' ? [effect.targetRoomId] : []));

	return {
		actorMoves: effects.flatMap((effect) => (effect.type === 'move_actor' ? [effect] : [])),
		playerRoutes: new Map(
			contentPack.rooms.map((room) => [
				room.roomId,
				[...room.exits.flatMap((exit) => (exit.targetRoomId === undefined ? [] : [exit.targetRoomId])), ...forcedMoveTargets],
			]),
		),
	};
};

/**
 * Does the follower ALWAYS refuse this room — an arm naming it with no
 * `when`, which no state of the world can open?
 *
 * @param actor - The actor
 * @param roomId - The room
 * @returns Whether no follow can ever bring the body in
 */
const alwaysRefuses = (actor: ActorDefinition, roomId: RoomId): boolean =>
	actor.follows?.refuses.some((refusal) => refusal.when === undefined && refusal.roomIds.includes(roomId)) === true;

/**
 * The rooms one actor can ever stand in: its starting room, every room a
 * `move_actor` names for it, and — for a follower — every room the player
 * can lead it to from those, walked to a fixed point. Empty for an actor
 * that is never on stage (a follower nothing ever places has nowhere to
 * follow from).
 *
 * @param actor - The actor
 * @param stage - The pack's stage directions ({@link stageDirectionsOf})
 * @returns The rooms, each once — declaration first, then the moves, then where it is led
 */
export const roomsAnActorCanStandIn = (actor: ActorDefinition, stage: StageDirections): readonly RoomId[] => {
	const rooms = new Set<RoomId>(actor.initialRoomId === undefined ? [] : [actor.initialRoomId]);

	for (const move of stage.actorMoves) {
		if (move.actorId === actor.actorId && move.destination !== 'offstage') {
			rooms.add(move.destination);
		}
	}

	if (actor.follows === undefined) {
		return [...rooms];
	}

	// A set's iterator visits what is added during the walk, so this is the
	// breadth-first walk to a fixed point: every room led to is a room the
	// body can be led ON from.
	for (const standingRoomId of rooms) {
		for (const nextRoomId of stage.playerRoutes.get(standingRoomId) ?? []) {
			if (!alwaysRefuses(actor, nextRoomId)) {
				rooms.add(nextRoomId);
			}
		}
	}

	return [...rooms];
};
